/**
 * COAActivation.jsx — AOS-FIN-COA-GLOBAL-02: Selective Account Activation
 *
 * Maker-checker workflow for activating platform reference accounts
 * into institutional operational Charts of Accounts.
 *
 * Maker: finance_manager, cfo, institution_admin
 * Checker: cfo, institution_admin, super_admin (must be ≠ requestor)
 * No self-approval enforced by backend (INV-COA-02-02).
 */
import React, { useCallback, useEffect, useState } from "react";
import { PageHeader } from "../components/layout/Shell";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import { useInstitution } from "../context/InstitutionContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  CheckCircle2,
  XCircle,
  Clock,
  Ban,
  Loader2,
  Plus,
  RefreshCw,
  Filter,
  Search,
  ChevronRight,
  ShieldCheck,
  BookOpen,
  Building2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("academiaos_token");

const STATUS_CONFIG = {
  PENDING: {
    label: "Pending Review",
    icon: Clock,
    color: "text-amber-600 dark:text-amber-400",
    badge: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800",
  },
  APPROVED: {
    label: "Approved",
    icon: CheckCircle2,
    color: "text-emerald-600 dark:text-emerald-400",
    badge: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
  },
  REJECTED: {
    label: "Rejected",
    icon: XCircle,
    color: "text-red-500",
    badge: "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-red-200",
  },
  CANCELLED: {
    label: "Cancelled",
    icon: Ban,
    color: "text-slate-400",
    badge: "bg-slate-100 dark:bg-slate-800 text-slate-500 border-slate-200",
  },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${cfg.badge}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

// ── New Activation Request Dialog ──────────────────────────────────────────────
function NewActivationDialog({ open, onClose, institutionId, onSuccess }) {
  const [step, setStep] = useState(1); // 1=search ref, 2=configure
  const [search, setSearch] = useState("");
  const [refs, setRefs] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [coas, setCoas] = useState([]);
  const [coaId, setCoaId] = useState("");
  const [proposedCode, setProposedCode] = useState("");
  const [nameOverride, setNameOverride] = useState("");
  const [saving, setSaving] = useState(false);

  const searchRefs = useCallback(async (q) => {
    setSearching(true);
    try {
      const res = await fetch(
        `${API}/api/finance/coa/reference-accounts?limit=20&search=${encodeURIComponent(q)}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (res.ok) setRefs((await res.json()).items || []);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (open && step === 1) searchRefs("");
  }, [open, step, searchRefs]);

  useEffect(() => {
    const timer = setTimeout(() => { if (step === 1) searchRefs(search); }, 300);
    return () => clearTimeout(timer);
  }, [search, step, searchRefs]);

  useEffect(() => {
    if (!open) { setStep(1); setSelected(null); setSearch(""); setProposedCode(""); setNameOverride(""); }
  }, [open]);

  const loadCoas = useCallback(async () => {
    try {
      const res = await fetch(
        `${API}/api/finance/coa/activations/institution-coas?institution_id=${institutionId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (res.ok) {
        const data = await res.json();
        const items = data.items || [];
        setCoas(items);
        if (items.length > 0) setCoaId(items[0].id);
      } else {
        toast.error("Could not load charts of accounts", {
          description: "Please ensure your institution has an active chart of accounts.",
        });
      }
    } catch {
      toast.error("Network error loading charts of accounts.");
    }
  }, [institutionId]);

  const selectRef = (ref) => {
    setSelected(ref);
    setProposedCode(ref.reference_code);
    setNameOverride("");
    setStep(2);
    loadCoas();
  };

  const handleSubmit = async () => {
    if (!selected || !coaId || !proposedCode.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/finance/coa/activations`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          reference_account_id: selected.id,
          chart_of_accounts_id: coaId,
          proposed_account_code: proposedCode.trim(),
          proposed_name_override: nameOverride.trim() || null,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        toast.success("Activation request submitted", {
          description: `Account '${selected.reference_code} – ${selected.account_name}' is pending review.`,
        });
        onSuccess?.();
        onClose();
      } else {
        toast.error("Submission failed", { description: data.detail || String(data) });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="coa-activation-new-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary" />
            {step === 1 ? "Select a Reference Account" : "Configure Activation"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {step === 1
              ? "Search the global reference library and select an account to activate."
              : `Configuring '${selected?.reference_code} – ${selected?.account_name}' for your institution.`}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by code, name or category…"
                className="pl-9"
                data-testid="coa-ref-search"
                autoFocus
              />
            </div>
            <div className="max-h-80 overflow-y-auto space-y-1 rounded-md border border-border bg-card/50">
              {searching && (
                <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />Loading…
                </div>
              )}
              {!searching && refs.length === 0 && (
                <div className="py-8 text-center text-sm text-muted-foreground">No accounts found.</div>
              )}
              {!searching && refs.map((ref) => (
                <button
                  key={ref.id}
                  onClick={() => selectRef(ref)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/60 transition-colors text-sm"
                  data-testid={`coa-ref-item-${ref.reference_code}`}
                >
                  <span className="font-mono text-xs bg-muted/80 px-1.5 py-0.5 rounded text-muted-foreground w-16 shrink-0">
                    {ref.reference_code}
                  </span>
                  <span className="flex-1 font-medium truncate">{ref.account_name}</span>
                  <Badge variant="outline" className="text-[10px] shrink-0">{ref.account_type}</Badge>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Reference account summary */}
            <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-xs bg-background px-1.5 py-0.5 rounded border">{selected?.reference_code}</span>
                <span className="font-semibold">{selected?.account_name}</span>
              </div>
              <div className="flex gap-3 text-xs text-muted-foreground">
                <span>Type: {selected?.account_type}</span>
                <span>Normal balance: {selected?.normal_balance}</span>
                <span>Category: {selected?.account_category}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium">
                  Account Code <span className="text-muted-foreground">(pre-filled, editable)</span>
                </label>
                <Input
                  value={proposedCode}
                  onChange={(e) => setProposedCode(e.target.value)}
                  placeholder="e.g. 1010"
                  className="font-mono"
                  data-testid="coa-activation-code"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium">
                  Name Override <span className="text-muted-foreground">(optional)</span>
                </label>
                <Input
                  value={nameOverride}
                  onChange={(e) => setNameOverride(e.target.value)}
                  placeholder="Leave blank to use reference name"
                  data-testid="coa-activation-name"
                />
              </div>
            </div>

            {coas.length > 1 && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium">Target Chart of Accounts</label>
                <Select value={coaId} onValueChange={setCoaId}>
                  <SelectTrigger data-testid="coa-activation-coa-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {coas.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="rounded-md bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 px-3 py-2 text-xs text-blue-700 dark:text-blue-300">
              <ShieldCheck className="h-3 w-3 inline mr-1" />
              This request will be submitted for independent review. A different authorized user must approve before the account is activated.
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 2 && (
            <Button variant="outline" onClick={() => setStep(1)} data-testid="coa-activation-back">
              Back
            </Button>
          )}
          <Button variant="outline" onClick={onClose} data-testid="coa-activation-cancel">Cancel</Button>
          {step === 2 && (
            <Button
              onClick={handleSubmit}
              disabled={!proposedCode.trim() || saving}
              data-testid="coa-activation-submit"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              Submit for Review
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review Dialog (approve / reject) ──────────────────────────────────────────
function ReviewDialog({ request, open, onClose, onSuccess }) {
  const [action, setAction] = useState("approve"); // "approve" | "reject"
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setAction("approve"); setNotes(""); }
  }, [open]);

  const handleSubmit = async () => {
    if (!request) return;
    setSaving(true);
    const endpoint = `${API}/api/finance/coa/activations/${request.id}/${action}`;
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ review_notes: notes.trim() || null }),
      });
      const data = await res.json();
      if (data.ok) {
        toast.success(action === "approve" ? "Request approved" : "Request rejected", {
          description: action === "approve"
            ? `Account '${request.proposed_account_code}' is now active.`
            : "Request has been rejected.",
        });
        onSuccess?.();
        onClose();
      } else {
        toast.error("Review failed", { description: data.detail || String(data) });
      }
    } finally {
      setSaving(false);
    }
  };

  if (!request) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="coa-review-dialog">
        <DialogHeader>
          <DialogTitle>Review Activation Request</DialogTitle>
          <DialogDescription className="text-xs">
            Account: <span className="font-mono font-semibold">{request.proposed_account_code}</span>
            {" – "}{request.effective_name}
            <br />Requested by {request.requested_by_email}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <Button
              variant={action === "approve" ? "default" : "outline"}
              size="sm"
              onClick={() => setAction("approve")}
              className="flex-1"
              data-testid="review-approve-btn"
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Approve
            </Button>
            <Button
              variant={action === "reject" ? "destructive" : "outline"}
              size="sm"
              onClick={() => setAction("reject")}
              className="flex-1"
              data-testid="review-reject-btn"
            >
              <XCircle className="h-3.5 w-3.5 mr-1.5" />Reject
            </Button>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium">
              Review Notes {action === "reject" && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={action === "approve"
                ? "Optional notes for the audit trail…"
                : "Reason for rejection (required)…"}
              className="w-full text-sm rounded-md border border-input bg-background px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-ring"
              data-testid="review-notes"
            />
          </div>

          <div className="rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
            Independent review required (INV-COA-02-02). You are not the requestor.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="review-cancel">Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={(action === "reject" && !notes.trim()) || saving}
            variant={action === "reject" ? "destructive" : "default"}
            data-testid="review-confirm-btn"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {action === "approve" ? "Approve Activation" : "Reject Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function COAActivation() {
  const { user } = useAuth();
  const { current, institutions, switchInstitution } = useInstitution();
  const isSuperAdmin = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [showNew, setShowNew] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);

  const load = useCallback(async () => {
    if (!current?.id) return;
    setLoading(true);
    try {
      const q = statusFilter !== "ALL" ? `&status=${statusFilter}` : "";
      const res = await fetch(
        `${API}/api/finance/coa/activations?institution_id=${current.id}&limit=100${q}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  }, [current?.id, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const pending = items.filter((i) => i.status === "PENDING").length;
  const approved = items.filter((i) => i.status === "APPROVED").length;

  if (!current) return null;

  return (
    <div data-testid="coa-activation-page">
      {/* Institution selector — visible only to super_admin with multiple institutions */}
      {isSuperAdmin && institutions.length > 1 && (
        <div className="px-6 lg:px-8 pt-4 pb-0">
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 max-w-xs">
            <Building2 className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
            <span className="text-xs text-amber-700 dark:text-amber-300 shrink-0">Institution:</span>
            <Select
              value={current.id}
              onValueChange={switchInstitution}
            >
              <SelectTrigger
                className="h-7 text-xs border-0 bg-transparent p-0 focus:ring-0 focus:ring-offset-0 shadow-none"
                data-testid="coa-institution-switcher"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {institutions.map((inst) => (
                  <SelectItem key={inst.id} value={inst.id} className="text-xs">
                    {inst.name || inst.short_name || inst.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      <PageHeader
        eyebrow="Finance · Chart of Accounts"
        title="Account Activation"
        description="Activate global reference accounts into your institution's operational chart of accounts. Requires independent review."
        actions={
          <>
            {pending > 0 && (
              <Badge
                variant="outline"
                className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-300 gap-1"
                data-testid="coa-pending-badge"
              >
                <Clock className="h-3 w-3" />
                {pending} pending
              </Badge>
            )}
            <Button
              size="sm"
              onClick={() => setShowNew(true)}
              data-testid="coa-activation-new-btn"
              className="gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" />
              New Activation
            </Button>
          </>
        }
      />

      <div className="p-6 lg:p-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {["ALL", "PENDING", "APPROVED", "REJECTED"].map((s) => {
            const count = s === "ALL" ? total
              : s === "PENDING" ? items.filter(i => i.status === "PENDING").length
              : s === "APPROVED" ? items.filter(i => i.status === "APPROVED").length
              : items.filter(i => i.status === "REJECTED").length;
            const cfg = STATUS_CONFIG[s] || { label: "All", icon: Filter, badge: "bg-muted border-border text-foreground" };
            const Icon = cfg.icon || Filter;
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`rounded-lg border p-3 text-left transition-all hover:shadow-sm ${
                  statusFilter === s
                    ? "ring-1 ring-primary border-primary bg-primary/5"
                    : "border-border bg-card hover:bg-muted/30"
                }`}
                data-testid={`coa-filter-${s.toLowerCase()}`}
              >
                <div className="text-2xl font-bold tabular-nums">{count}</div>
                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                  {s !== "ALL" && <Icon className="h-3 w-3" />}
                  {s === "ALL" ? "Total requests" : cfg.label}
                </div>
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="text-sm font-medium">Activation Requests</span>
            <Button size="sm" variant="ghost" onClick={load} className="h-7 gap-1">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>

          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center">
              <BookOpen className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">No activation requests.</p>
              <Button size="sm" variant="outline" className="mt-3" onClick={() => setShowNew(true)}
                data-testid="coa-empty-new-btn">
                <Plus className="h-3.5 w-3.5 mr-1" />New Activation
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="px-4 py-2.5 text-left font-medium">Reference</th>
                    <th className="px-4 py-2.5 text-left font-medium">Account</th>
                    <th className="px-4 py-2.5 text-left font-medium">Type</th>
                    <th className="px-4 py-2.5 text-left font-medium">Status</th>
                    <th className="px-4 py-2.5 text-left font-medium">Requested by</th>
                    <th className="px-4 py-2.5 text-left font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-muted/30 transition-colors"
                      data-testid={`coa-req-row-${item.status?.toLowerCase()}`}>
                      <td className="px-4 py-2.5">
                        <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                          {item.reference_account?.reference_code || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="font-medium">{item.proposed_account_code}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-[200px]">{item.effective_name}</div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-xs text-muted-foreground">{item.reference_account?.account_type}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <StatusBadge status={item.status} />
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">
                        <div>{item.requested_by_email?.split("@")[0]}</div>
                        <div className="opacity-60">
                          {item.requested_at ? new Date(item.requested_at).toLocaleDateString() : "—"}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        {item.status === "PENDING" && (
                          <div className="flex gap-1.5">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => setReviewTarget(item)}
                              data-testid={`coa-review-btn-${item.id}`}
                            >
                              Review
                            </Button>
                          </div>
                        )}
                        {item.status === "APPROVED" && item.activated_account_id && (
                          <span className="text-xs text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 className="h-3.5 w-3.5 inline mr-1" />
                            Activated
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* New Activation Dialog */}
      <NewActivationDialog
        open={showNew}
        onClose={() => setShowNew(false)}
        institutionId={current?.id}
        onSuccess={load}
      />

      {/* Review Dialog */}
      <ReviewDialog
        open={!!reviewTarget}
        request={reviewTarget}
        onClose={() => setReviewTarget(null)}
        onSuccess={() => { setReviewTarget(null); load(); }}
      />
    </div>
  );
}
